import React from 'react';
import {TextStyle, View, ViewStyle} from 'react-native';
import {isEmpty, isFunction, noop} from 'lodash';

import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import {generateCallback} from '/util/component.util';

import {styles} from './GroupItemList.style';
import {normalizeOptions} from './GroupItemList.util';

interface Option {
  label: React.ReactNode;
  value: any;
  selected: boolean;
}

interface GroupedOption {
  label: React.ReactNode;
  options: Option[];
}

interface GroupItemListProps {
  style: {
    base?: ViewStyle;
    groupItems?: ViewStyle;
    groupLabel?: ViewStyle;
    item?: ViewStyle;
    groupLabelText?: TextStyle;
  };
  onOptionPress: (params: {
    option: Option;
    optionIndex: number;
    groupIndex: number;
  }) => void;
  options: Option[] | GroupedOption[];
  renderOptionItem: (
    option: Option,
    optionIndex: number,
    groupIndex: number,
  ) => {
    label: React.ReactNode;
    prefix: React.ReactNode;
    suffix: React.ReactNode;
  };
  isFirstItemWithBorder: boolean;
  isLabel: boolean;
}

export const GroupItemList: React.FC<GroupItemListProps> = ({
  style: styleProps = {},
  onOptionPress = noop,
  options = [],
  isFirstItemWithBorder = false,
  renderOptionItem: renderOptionItemProp,
  isLabel = true,
}: GroupItemListProps) => {
  const renderOptionItem = (option: any) => {
    const {label} = option;

    return {
      label: <Text style={styles.groupItemText}>{label}</Text>,
      prefix: null,
      suffix: null,
    };
  };

  const renderOption = (option: any, optionIndex: any, groupIndex: any) => {
    const {label, prefix, suffix} = isFunction(renderOptionItemProp)
      ? renderOptionItemProp(option, optionIndex, groupIndex)
      : // @ts-expect-error TS(2554): Expected 1 arguments, but got 3.
        renderOptionItem(option, optionIndex, groupIndex);

    const listItemStyle = {};
    if (isFirstItemWithBorder || optionIndex > 0) {
      // @ts-expect-error TS(2339): Property 'base' does not exist on type '{}'.
      listItemStyle.base = styles.groupItemWithBorder;
    }

    // @ts-expect-error TS(2339): Property 'base' does not exist on type '{}'.
    listItemStyle.base = {...listItemStyle.base, ...styleProps.item};

    return (
      <ListItem
        key={optionIndex}
        label={label}
        prefix={prefix}
        suffix={suffix}
        style={listItemStyle}
        onPress={generateCallback({
          func: onOptionPress,
          params: {...option, optionIndex, groupIndex},
        })}
      />
    );
  };

  const renderGroup = (group: any, groupIndex: any) => {
    const {label, options: groupOptions = []} = group;

    const groupItemsStyle = [styles.groupItems];
    groupItemsStyle.push(styleProps.groupItems);

    return (
      <View key={groupIndex}>
        {!isEmpty(label) && isLabel && (
          <View style={styleProps.groupLabel}>
            <Text
              variant="subheading"
              emphasis="low"
              style={styles.groupLabelText}>
              {label}
            </Text>
          </View>
        )}
        <View style={groupItemsStyle}>
          {groupOptions.map((option: any, optionIndex: any) =>
            renderOption(option, optionIndex, groupIndex),
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={styleProps.base}>
      {normalizeOptions(options).map(renderGroup)}
    </View>
  );
};

export default GroupItemList;
